import { DepartmentResource } from '../../../entities/Departments/Departments'
import { DocumentViewModel, Role, UserDocumentViewModel } from '../../../models'
import { authorizedFetch } from '../../helpers/authorizedFetch'
import trans from '../../helpers/trans'
import { generateApiV3Url } from '../../helpers/url'
import { FetchResult, FetchResultType } from '../FetchResult'
import {
    FileFormData,
    FilesMetaResponse,
    FileV3Response,
    FileViewModel,
    GetFilesParams,
} from './File'
import {
    transformFileFormDataToV3Request,
    transformFileV3ResponseToDocumentViewModel,
    transformFileV3ResponseToFileViewModel,
    transformFileV3ResponseToUserDocumentViewModel,
} from './FileTransformers'

/**
 * Build query params from GetFilesParams
 */
const buildFilesQueryParams = (
    params: GetFilesParams,
): Record<string, string | number | string[] | boolean> => {
    const queryParams: Record<string, string | number | string[] | boolean> = {}

    if (params.search) {
        queryParams.search = params.search
    }
    if (params.documentTypes && params.documentTypes.length > 0) {
        queryParams['documentTypes[]'] = params.documentTypes
    }
    if (params.departments && params.departments.length > 0) {
        queryParams['departments[]'] = params.departments
    }
    if (params.mainDepartments && params.mainDepartments.length > 0) {
        queryParams['mainDepartments[]'] = params.mainDepartments
    }
    if (params.roles && params.roles.length > 0) {
        queryParams['roles[]'] = params.roles
    }
    if (params.fileRoles && params.fileRoles.length > 0) {
        queryParams['fileRoles[]'] = params.fileRoles
    }
    if (params.ownerUuids && params.ownerUuids.length > 0) {
        queryParams['ownerUuids[]'] = params.ownerUuids
    }
    if (params.linkedUserUuids && params.linkedUserUuids.length > 0) {
        queryParams['linkedUserUuids'] = params.linkedUserUuids
    }
    if (params.notLinkedUsers === true) {
        queryParams.notLinkedUsers = true
    }
    if (params.start !== undefined) {
        queryParams.start = params.start
    }
    if (params.items !== undefined) {
        queryParams.items = params.items
    }
    if (params.orderBy) {
        queryParams.orderBy = params.orderBy
    }
    if (params.direction) {
        queryParams.direction = params.direction
    }

    return queryParams
}

/**
 * GET /api/v3/files
 * Fetch all files with optional filters (data only, no meta)
 */
export const getFilesApiCall = async (
    params: GetFilesParams,
): Promise<FetchResult<FileViewModel[], string>> => {
    try {
        const queryParams = buildFilesQueryParams(params)

        const url = generateApiV3Url({
            endpoint: '/files',
            queryParams,
        })

        const response = await authorizedFetch(url)
        const json = await response.json()

        if (! response.ok) {
            return {
                status: response.status,
                type: FetchResultType.Error,
                error: json.message || trans('errors.unknownError'),
            }
        }

        const files = (Array.isArray(json) ? json : json.data || []).map(
            transformFileV3ResponseToFileViewModel,
        )

        return {
            status: response.status,
            type: FetchResultType.Success,
            data: files,
        }
    } catch (error) {
        console.error('[getFilesApiCall]', error)

        return {
            status: 500,
            type: FetchResultType.Error,
            error: trans('errors.unknownError'),
        }
    }
}

/**
 * GET /api/v3/files/meta
 * Fetch files pagination metadata
 */
export const getFilesMetaApiCall = async (
    params: GetFilesParams,
): Promise<FetchResult<FilesMetaResponse, string>> => {
    try {
        const queryParams = buildFilesQueryParams(params)

        const url = generateApiV3Url({
            endpoint: '/files/meta',
            queryParams,
        })

        const response = await authorizedFetch(url)
        const json = await response.json()

        if (! response.ok) {
            return {
                status: response.status,
                type: FetchResultType.Error,
                error: json.message || trans('errors.unknownError'),
            }
        }

        return {
            status: response.status,
            type: FetchResultType.Success,
            data: json as FilesMetaResponse,
        }
    } catch (error) {
        console.error('[getFilesMetaApiCall]', error)

        return {
            status: 500,
            type: FetchResultType.Error,
            error: trans('errors.unknownError'),
        }
    }
}

/**
 * GET /api/v3/files - returning DocumentViewModel[] for PZ page compatibility
 */
export const getFilesAsDocumentsApiCall = async (
    params: GetFilesParams,
): Promise<FetchResult<DocumentViewModel[], string>> => {
    try {
        const queryParams = buildFilesQueryParams(params)

        const url = generateApiV3Url({
            endpoint: '/files',
            queryParams,
        })

        const response = await authorizedFetch(url)
        const json = await response.json()

        if (! response.ok) {
            return {
                status: response.status,
                type: FetchResultType.Error,
                error: json.message || trans('errors.unknownError'),
            }
        }

        const documents = (Array.isArray(json) ? json : json.data || []).map(
            transformFileV3ResponseToDocumentViewModel,
        )

        return {
            status: response.status,
            type: FetchResultType.Success,
            data: documents,
        }
    } catch (error) {
        console.error('[getFilesAsDocumentsApiCall]', error)

        return {
            status: 500,
            type: FetchResultType.Error,
            error: trans('errors.unknownError'),
        }
    }
}

/**
 * GET /api/v3/files - returning UserDocumentViewModel[] for management page
 */
export const getFilesForUserApiCall = async (
    userId: string,
    userDepartments: DepartmentResource[],
    userRoles: Role[],
    params: GetFilesParams = {},
): Promise<FetchResult<UserDocumentViewModel[], string>> => {
    try {
        const queryParams = buildFilesQueryParams({
            ...params,
            linkedUserUuids: [userId],
        })

        const url = generateApiV3Url({
            endpoint: '/files',
            queryParams,
        })

        const response = await authorizedFetch(url)
        const json = await response.json()

        if (! response.ok) {
            return {
                status: response.status,
                type: FetchResultType.Error,
                error: json.message || trans('errors.unknownError'),
            }
        }

        const documents = (Array.isArray(json) ? json : json.data || []).map(
            (file: FileV3Response) =>
                transformFileV3ResponseToUserDocumentViewModel(file, userId, userDepartments, userRoles),
        )

        return {
            status: response.status,
            type: FetchResultType.Success,
            data: documents,
        }
    } catch (error) {
        console.error('[getFilesForUserApiCall]', error)

        return {
            status: 500,
            type: FetchResultType.Error,
            error: trans('errors.unknownError'),
        }
    }
}

/**
 * GET /api/v3/files/{uuid}
 * Fetch a single file by ID
 */
export const getFileApiCall = async (
    uuid: string,
): Promise<FetchResult<FileViewModel, string>> => {
    try {
        const url = generateApiV3Url({
            endpoint: `/files/${uuid}`,
        })

        const response = await authorizedFetch(url)
        const json = await response.json()

        if (! response.ok) {
            return {
                status: response.status,
                type: FetchResultType.Error,
                error: json.message || trans('errors.unknownError'),
            }
        }

        const file = transformFileV3ResponseToFileViewModel(json.data || json)

        return {
            status: response.status,
            type: FetchResultType.Success,
            data: file,
        }
    } catch (error) {
        console.error('[getFileApiCall]', error)

        return {
            status: 500,
            type: FetchResultType.Error,
            error: trans('errors.unknownError'),
        }
    }
}

/**
 * POST /api/v3/files
 * Create a new file, returns DocumentViewModel for PZ documents list
 */
export const postDocumentApiCall = async (
    data: FileFormData,
): Promise<FetchResult<DocumentViewModel, string>> => {
    try {
        const body = transformFileFormDataToV3Request(data)

        const url = generateApiV3Url({
            endpoint: '/files',
        })

        const response = await authorizedFetch(url, {
            method: 'POST',
            body: JSON.stringify(body),
        })

        const json = await response.json()

        if (! response.ok) {
            return {
                status: response.status,
                type: FetchResultType.Error,
                error: json.message || trans('errors.unknownError'),
            }
        }

        const document = transformFileV3ResponseToDocumentViewModel(json.data || json)

        return {
            status: response.status,
            type: FetchResultType.Success,
            data: document,
        }
    } catch (error) {
        console.error('[postDocumentApiCall]', error)

        return {
            status: 500,
            type: FetchResultType.Error,
            error: trans('errors.unknownError'),
        }
    }
}

/**
 * POST /api/v3/files
 * Create a new file, returns UserDocumentViewModel for management page
 */
export const postUserDocumentApiCall = async (
    data: FileFormData,
    userId: string,
    userDepartments: DepartmentResource[],
    userRoles: Role[],
): Promise<FetchResult<UserDocumentViewModel, string>> => {
    try {
        const body = transformFileFormDataToV3Request(data)

        const url = generateApiV3Url({
            endpoint: '/files',
        })

        const response = await authorizedFetch(url, {
            method: 'POST',
            body: JSON.stringify(body),
        })

        const json = await response.json()

        if (! response.ok) {
            return {
                status: response.status,
                type: FetchResultType.Error,
                error: json.message || trans('errors.unknownError'),
            }
        }

        const document = transformFileV3ResponseToUserDocumentViewModel(
            json.data || json,
            userId,
            userDepartments,
            userRoles,
        )

        return {
            status: response.status,
            type: FetchResultType.Success,
            data: document,
        }
    } catch (error) {
        console.error('[postUserDocumentApiCall]', error)

        return {
            status: 500,
            type: FetchResultType.Error,
            error: trans('errors.unknownError'),
        }
    }
}

/**
 * PATCH /api/v3/files/{uuid}
 * Update an existing file, returns DocumentViewModel for PZ documents list
 */
export const patchDocumentApiCall = async (
    uuid: string,
    data: FileFormData,
): Promise<FetchResult<DocumentViewModel, string>> => {
    try {
        const body = transformFileFormDataToV3Request(data)

        const url = generateApiV3Url({
            endpoint: `/files/${uuid}`,
        })

        const response = await authorizedFetch(url, {
            method: 'PATCH',
            body: JSON.stringify(body),
        })

        const json = await response.json()

        if (! response.ok) {
            return {
                status: response.status,
                type: FetchResultType.Error,
                error: json.message || trans('errors.unknownError'),
            }
        }

        const document = transformFileV3ResponseToDocumentViewModel(json.data || json)

        return {
            status: response.status,
            type: FetchResultType.Success,
            data: document,
        }
    } catch (error) {
        console.error('[patchDocumentApiCall]', error)

        return {
            status: 500,
            type: FetchResultType.Error,
            error: trans('errors.unknownError'),
        }
    }
}

/**
 * PATCH /api/v3/files/{uuid}
 * Update an existing file, returns UserDocumentViewModel for management page
 */
export const patchUserDocumentApiCall = async (
    uuid: string,
    data: FileFormData,
    userId: string,
    userDepartments: DepartmentResource[],
    userRoles: Role[],
): Promise<FetchResult<UserDocumentViewModel, string>> => {
    try {
        const body = transformFileFormDataToV3Request(data)

        const url = generateApiV3Url({
            endpoint: `/files/${uuid}`,
        })

        const response = await authorizedFetch(url, {
            method: 'PATCH',
            body: JSON.stringify(body),
        })

        const json = await response.json()

        if (! response.ok) {
            return {
                status: response.status,
                type: FetchResultType.Error,
                error: json.message || trans('errors.unknownError'),
            }
        }

        const document = transformFileV3ResponseToUserDocumentViewModel(
            json.data || json,
            userId,
            userDepartments,
            userRoles,
        )

        return {
            status: response.status,
            type: FetchResultType.Success,
            data: document,
        }
    } catch (error) {
        console.error('[patchUserDocumentApiCall]', error)

        return {
            status: 500,
            type: FetchResultType.Error,
            error: trans('errors.unknownError'),
        }
    }
}

/**
 * DELETE /api/v3/files/{uuid}
 * Delete a file
 */
export const deleteFileApiCall = async (
    uuid: string,
): Promise<FetchResult<void, string>> => {
    try {
        const url = generateApiV3Url({
            endpoint: `/files/${uuid}`,
        })

        const response = await authorizedFetch(url, {
            method: 'DELETE',
        })

        if (! response.ok) {
            const json = await response.json()
            return {
                status: response.status,
                type: FetchResultType.Error,
                error: json.message || trans('errors.unknownError'),
            }
        }

        return {
            status: response.status,
            type: FetchResultType.Success,
            data: undefined,
        }
    } catch (error) {
        console.error('[deleteFileApiCall]', error)

        return {
            status: 500,
            type: FetchResultType.Error,
            error: trans('errors.unknownError'),
        }
    }
}
